<template>
  <component :is="tag"
      class="dropdown nav-item"
      :class="{show: isOpen, 'dropup': isDropup}"
      aria-haspopup="listbox"
      :aria-expanded="isOpen.toString()"
      @click="toggleDropDown"
      @keydown="onKeydown"
      v-click-outside="closeDropDown">
    <a class="nav-link dropdown-toggle"
       data-toggle="dropdown"
       role="button"
       tabindex="0"
       :aria-expanded="isOpen.toString()"
       aria-haspopup="listbox"
       @keydown.enter.prevent="toggleDropDown($event)"
       @keydown.space.prevent="toggleDropDown($event)">
      <slot name="title">
        <i :class="icon" aria-hidden="true"></i>
        <span class="no-icon">{{title}}</span>
      </slot>
    </a>
    <div class="dropdown-menu" :class="{'show': isOpen}" role="listbox" :id="menuId" tabindex="-1" :aria-activedescendant="activeDescendantId || undefined" :aria-hidden="(!isOpen).toString()">
      <slot></slot>
    </div>
  </component>
</template>

<script>
import clickOutside from '../directives/click-outside';
import { defineComponent } from 'vue';

const dropdownRegistry = {
  activeDropdown: null,
  register(instance) {
    if (this.activeDropdown && this.activeDropdown !== instance) {
      this.activeDropdown.closeDropDown();
    }
    this.activeDropdown = instance;
  },
  unregister(instance) {
    if (this.activeDropdown === instance) {
      this.activeDropdown = null;
    }
  }
};

export default defineComponent({
  name: 'BaseDropdown',
  props: {
    title: String,
    icon: String,
    tag: {
      type: String,
      default: 'li'
    }
  },
  data() {
    return {
      isOpen: false,
      isDropup: false,
      activeIndex: -1,
      menuId: `bd-menu-${Math.random().toString(36).slice(2, 9)}`
    };
  },
  computed: {
    activeDescendantId() {
      if (this.activeIndex < 0 || !this.isOpen) return '';
      return `${this.menuId}-opt-${this.activeIndex}`;
    }
  },
  methods: {
    toggleDropDown(event) {
      const e = event || null;
      if (!this.isOpen) {
        dropdownRegistry.register(this);
      } else {
        dropdownRegistry.unregister(this);
      }
      this.isOpen = !this.isOpen;
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      this.$emit('change', this.isOpen);
      if (this.isOpen) {
        this.activeIndex = -1;
        this.adjustDropdownPosition();
        this.$nextTick(() => this.syncOptionRoles());
      } else {
        this.activeIndex = -1;
      }
    },
    closeDropDown() {
      if (this.isOpen) {
        this.isOpen = false;
        this.isDropup = false;
        this.activeIndex = -1;
        this.$emit('change', this.isOpen);
        dropdownRegistry.unregister(this);
      }
    },
    adjustDropdownPosition() {
      this.$nextTick(() => {
        const menu = this.$el.querySelector('.dropdown-menu');
        if (!menu) return;
        const menuRect = menu.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropup = menuRect.bottom > viewportHeight;
      });
    },
    syncOptionRoles() {
      const menu = this.$el.querySelector('.dropdown-menu');
      if (!menu) return;
      const items = menu.querySelectorAll(':scope > *');
      items.forEach((el, idx) => {
        if (!el.hasAttribute('role')) el.setAttribute('role', 'option');
        el.setAttribute('id', `${this.menuId}-opt-${idx}`);
        el.setAttribute('aria-selected', idx === this.activeIndex ? 'true' : 'false');
        el.setAttribute('tabindex', '-1');
      });
    },
    onKeydown(event) {
      if (!this.isOpen && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.toggleDropDown(event);
        return;
      }
      if (!this.isOpen) return;
      const menu = this.$el.querySelector('.dropdown-menu');
      const items = menu ? Array.from(menu.querySelectorAll('[role="option"]')) : [];
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropDown();
        const toggle = this.$el.querySelector('.dropdown-toggle');
        if (toggle) toggle.focus();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = Math.min(items.length - 1, this.activeIndex + 1);
        this.syncOptionRoles();
        if (items[this.activeIndex]) items[this.activeIndex].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(0, this.activeIndex - 1);
        this.syncOptionRoles();
        if (items[this.activeIndex]) items[this.activeIndex].focus();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.activeIndex >= 0 && items[this.activeIndex]) {
          items[this.activeIndex].click();
        }
        this.closeDropDown();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.activeIndex = 0;
        this.syncOptionRoles();
        if (items[0]) items[0].focus();
      } else if (event.key === 'End') {
        event.preventDefault();
        this.activeIndex = items.length - 1;
        this.syncOptionRoles();
        if (items[this.activeIndex]) items[this.activeIndex].focus();
      }
    }
  },
  beforeUnmount() {
    dropdownRegistry.unregister(this);
  },
  directives: {
    clickOutside
  }
});
</script>

<style scoped>
  .dropdown .dropdown-toggle{
    cursor: pointer;
  }
  .dropdown .dropdown-toggle:focus-visible {
    outline: 2px solid var(--adm-accent, #4f46e5);
    outline-offset: 2px;
    border-radius: 4px;
  }
  .dropdown-menu [role="option"]:focus-visible {
    outline: 2px solid var(--adm-accent, #4f46e5);
    outline-offset: -2px;
    background: var(--adm-accent-soft, rgba(79,70,229,0.12));
  }
  .dropup .dropdown-menu {
    top: auto;
    bottom: 100%;
  }
</style>
